import { ReactElement, useState, useEffect } from "react"
import { ValidationRules, useFormContext, useWatch } from "react-hook-form"
// import style manually
import "react-markdown-editor-lite/lib/index.css"
import colors from "~constants/colors"
import MdEditor from "react-markdown-editor-lite"
import MarkdownIt from "markdown-it"
import { InputContainer } from "~styled/input"

interface MarkdownEditorProps {
  name: string
  label: string
  infoCopy?: string
  className?: string
  maxLength?: number
  placeholder?: string
  errorMessage: string
  required?: boolean
  setValue: (field: string, val: unknown, config?: Object) => void
  register: (fields: any, rules?: ValidationRules) => void
  getValues: (field: string) => any
  unregister: (field: string) => void
}

const editorStyles = (focused: boolean = false, error: boolean = false) => {
  const borderColor = error
    ? colors.red
    : focused
      ? colors.caribbean
      : colors.gray2

  return {
    borderBottom: `1px solid ${borderColor}`,
    minHeight: "200px",
    padding: "0 10px",
  }
}

const MarkdownEditor = (props: MarkdownEditorProps) => {
  // Component state
  const [markdownValue, setMarkdownValue] = useState<string>()
  const [isFocused] = useState<boolean>(false)

  // Props and defaults
  const {
    name,
    label,
    infoCopy,
    className,
    errorMessage,
    required = false,
    setValue,
    register,
    getValues,
    unregister,
  } = props

  // react-hook-form's control isn't passed via props, but every caller
  // wraps this editor in a FormProvider, so we can subscribe to changes
  // for `name` and react to external updates (e.g. reset() in edit flows).
  const { control } = useFormContext()
  const watchedValue = useWatch({ control, name })

  // Effect to register and unregister the custom form control
  useEffect(() => {
    register(name, { required })
    setMarkdownValue(getValues(name) || "")
    return () => unregister(name)
  }, [name, register, unregister])

  // Sync external form value changes (e.g. reset() when editing an existing
  // record) into the editor's local state. Without this, the editor renders
  // empty even though the form holds the prefilled value.
  useEffect(() => {
    const next = watchedValue ?? ""
    if (next !== (markdownValue ?? "")) {
      setMarkdownValue(next)
    }
  }, [watchedValue])

  // Method to display "error message" or "info copy"
  const showErrorOrInfo = (): ReactElement => {
    if (errorMessage) {
      return <span className="error-message">{errorMessage}</span>
    }
    return <span className="info-copy">{infoCopy}</span>
  }

  // Method to handle editor change
  const onChange = (v: { text: string; html: string }) => {
    setMarkdownValue(v.text || "")
    setValue(name, v.text, { shouldValidate: true })
  }

  const mdParser = new MarkdownIt({
    html: true,
    sanitize: false,
  })

  return (
    <InputContainer className={className}>
      <span className="label">{label}</span>
      <MdEditor
        style={editorStyles(isFocused, !!errorMessage)}
        value={markdownValue}
        onChange={onChange}
        renderHTML={text => mdParser.render(text)}
      />
      <div className="input-bottom">
        {/* Show error message or info */}
        {showErrorOrInfo()}
      </div>
    </InputContainer>
  )
}

export default MarkdownEditor
